import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import config from 'src/config';
import { useInvokeSpotlightJobMutation } from 'src/store/spotlight-api';

interface LalFormData {
  countryCode: string;
  partnerName: string;
  seedSegmentId: string;
  seedSegmentType: string;
  audienceName: string;
  exclusionListSegmentId: string;
  exclusionListSegmentType: string;
  advertiserAccountIds: string;
}

const COUNTRY_OPTIONS = [
  { label: 'Australia', value: 'AU' },
  { label: 'Brazil', value: 'BR' },
  { label: 'Canada', value: 'CA' },
  { label: 'France', value: 'FR' },
  { label: 'Germany', value: 'DE' },
  { label: 'Italy', value: 'IT' },
  { label: 'Japan', value: 'JP' },
  { label: 'Mexico', value: 'MX' },
  { label: 'New Zealand', value: 'NZ' },
  { label: 'Philippines', value: 'PH' },
  { label: 'Poland', value: 'PL' },
  { label: 'Spain', value: 'ES' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'United States', value: 'US' },
];

const PROD_COUNTRY_CODES = new Set([
  'AU',
  'BR',
  'CA',
  'DE',
  'ES',
  'FR',
  'GB',
  'IT',
  'JP',
  'MX',
  'NZ',
  'PH',
  'US',
]);

export const getCountryOptions = () =>
  config.stage === 'prod'
    ? COUNTRY_OPTIONS.filter((c) => PROD_COUNTRY_CODES.has(c.value))
    : COUNTRY_OPTIONS;

const PARTNER_OPTIONS = [
  { label: 'TikTok', value: 'tiktok' },
];

/**
 * Extracts the execution ID from a Step Functions execution ARN.
 *
 * Step Functions execution ARN format:
 *   arn:aws:states:<region>:<account>:execution:<state-machine-name>:<execution-id>
 *
 * The execution ID is the segment after the last colon.
 * Returns `null` for null/undefined/empty input. If the input contains no colon
 * (unexpected shape), it is returned unchanged.
 */
export const parseExecutionId = (executionArn: string | null | undefined): string | null => {
  if (!executionArn) return null;
  const lastColonIdx = executionArn.lastIndexOf(':');
  return lastColonIdx >= 0 ? executionArn.slice(lastColonIdx + 1) : executionArn;
};

export const SpotlightJobCreationForm = () => {
  const { t } = useTranslation();
  const { handleSubmit, control, reset, formState: { errors, isValid, isSubmitting } } = useForm<LalFormData>({
    defaultValues: {
      countryCode: '',
      partnerName: 'tiktok',
      seedSegmentId: '',
      seedSegmentType: 'Bullseye',
      audienceName: '',
      exclusionListSegmentId: '',
      exclusionListSegmentType: '',
      advertiserAccountIds: '',
    },
    mode: 'onChange',
  });

  const [submitted, setSubmitted] = useState(false);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [invokeSpotlightJob, { isLoading }] = useInvokeSpotlightJobMutation();

  const onSubmit = async (data: LalFormData) => {
    setSubmitError(null);
    setSubmitted(false);
    setExecutionId(null);
    try {
      const response = await invokeSpotlightJob({
        countryCode: data.countryCode,
        workflowData: {
          partnerName: data.partnerName,
          seedSegmentId: data.seedSegmentId,
          seedSegmentType: 'bullseye',
          exclusionListSegmentId: data.exclusionListSegmentId?.trim() || '',
          exclusionListSegmentType: 'bullseye',
          audienceName: data.audienceName,
          advertiserAccountIds: data.advertiserAccountIds
            ? data.advertiserAccountIds.split(',').map(id => id.trim()).filter(Boolean)
            : [],
        },
      }).unwrap();
      setSubmitted(true);
      setExecutionId(parseExecutionId(response?.body?.executionArn));
      reset();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string }; message?: string };
      setSubmitError(error?.data?.message ?? error?.message ?? t('spotlight_form_error_default'));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Column spacing="400">
        <Box spacingInset="400" type="outline">
          <Column spacing="400">
              <Row alignmentVertical="top" spacing="600" widths="fill" wrap="down">
                <Column>
                  <Controller
                    control={control}
                    name="countryCode"
                    render={({ field }) => (
                      <Select label={t('spotlight_form_label_country')} placeholder={t('spotlight_form_placeholder_country')} value={field.value} onChange={field.onChange}>
                        {getCountryOptions().map(({ label, value }) => (
                          <SelectOption key={value} label={label} value={value} />
                        ))}
                      </Select>
                    )}
                    rules={{ required: t('spotlight_form_error_country_required') }}
                  />
                  {errors.countryCode && <Text color="error">{errors.countryCode.message}</Text>}
                </Column>

                <Column>
                  <Controller
                    control={control}
                    name="partnerName"
                    render={({ field }) => (
                      <Select label={t('spotlight_form_label_partner')} value={field.value} onChange={field.onChange}>
                        {PARTNER_OPTIONS.map(({ label, value }) => (
                          <SelectOption key={value} label={label} value={value} />
                        ))}
                      </Select>
                    )}
                    rules={{ required: t('spotlight_form_error_partner_required') }}
                  />
                </Column>

              </Row>

              <Row alignmentVertical="top" spacing="600" widths="fill" wrap="down">
                <Column>
                  <Controller
                    control={control}
                    name="audienceName"
                    render={({ field }) => (
                      <Input
                        error={!!errors.audienceName}
                        errorMessage={errors.audienceName?.message}
                        label={t('spotlight_form_label_audience_name')}
                        placeholder={t('spotlight_form_placeholder_audience_name')}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                    rules={{ required: t('spotlight_form_error_audience_name_required'), validate: v => v.trim() !== '' || t('spotlight_form_error_cannot_be_blank') }}
                  />
                </Column>
              </Row>

              <Row alignmentVertical="top" spacing="600" widths="fill" wrap="down">
                <Column>
                  <Controller
                    control={control}
                    name="seedSegmentId"
                    render={({ field }) => (
                      <Input
                        error={!!errors.seedSegmentId}
                        errorMessage={errors.seedSegmentId?.message}
                        label={t('spotlight_form_label_seed_segment_id')}
                        placeholder={t('spotlight_form_placeholder_seed_segment_id')}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                    rules={{ required: t('spotlight_form_error_segment_id_required'), validate: v => v.trim() !== '' || t('spotlight_form_error_cannot_be_blank') }}
                  />
                </Column>
              </Row>

              <Row alignmentVertical="top" spacing="600" widths="fill" wrap="down">
                <Column>
                  <Controller
                    control={control}
                    name="exclusionListSegmentId"
                    render={({ field }) => (
                      <Input
                        label={t('spotlight_form_label_exclusion_segment_id')}
                        placeholder={t('spotlight_form_placeholder_exclusion_segment_id')}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Column>
              </Row>

              <Row spacing="600" widths="fill" wrap="down">
                <Column>
                  <Controller
                    control={control}
                    name="advertiserAccountIds"
                    render={({ field }) => (
                      <Input
                        label={t('spotlight_form_label_advertiser_account_ids')}
                        placeholder={t('spotlight_form_placeholder_advertiser_account_ids')}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Column>
              </Row>
            </Column>
          </Box>

          <Row alignmentHorizontal="start" spacing="200">
            <Button
              data-testid="spotlight-form-submit-button"
              disabled={!isValid || isLoading || isSubmitting}
              submit
              type="primary"
            >
              {isLoading ? <Loader size="small" /> : t('spotlight_form_submit')}
            </Button>
          </Row>

          {submitted && (
            <Column spacing="100">
              <Text>{t('spotlight_form_success')}</Text>
              {executionId && (
                <Text>{`${t('spotlight_form_success_execution_id')} ${executionId}`}</Text>
              )}
            </Column>
          )}
          {submitError && (
            <Text color="error">{submitError}</Text>
          )}
        </Column>
      </form>
  );
};
